
import { Dialog } from 'primereact/dialog';
import React, { useEffect, useMemo, useState, useRef } from 'react';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';

import CoachingToDo from '../../../components/CoachingToDo/CoachingToDo';
import CoachingShoutOut from '../../../components/coachingShoutOut/coachingShoutOUt';
import { selectCurrentUserManagerLevel } from '../../../pages/login/authSlice';
import { TriadFirstTab } from './TriadFirstTab';
import { TriadHeader } from './TriadHeader';
import { TriadSecondTab } from './TriadSecondTab';
import { TriadThirdTab } from './TriadThirdTab';
import { TriadSecondTabV2 } from '../coachingObservationV2/TriadSecondTabV2';
import './triad.scss';
import { useCreateTriadMutation, useGetTriadQuery, useGetTriadQuestionsQuery } from './triad.slice';
import { TriadThirdTabV2 } from '../coachingObservationV2/TriadThirdTabV2';

type ActiveTab = 'first' | 'second' | 'third' | 'newForm' | 'secondV2' | 'thirdV2';
interface TriadHomeProps {
    OnDisplayModal: (displayModal: boolean) => void;
    displayModal: boolean;
    loggedEmp: any;
    month?: number;
    year?: number;
    date: Date;
    hirearchy: any;
    loggedEmpNo: string;
    triadRecordId?: number | null;
    isConfiguredNewTriad: boolean;
}

const useVersionWeighting = (triadVersionId: number | undefined): boolean => {
    const { data: triadQuestions } = useGetTriadQuestionsQuery(undefined);
    return useMemo(() => {
        const versionData = triadQuestions?.data?.find(
            (v: any) => v.triad_version_id === triadVersionId
        );
        return versionData?.use_section_weighting ?? false;
    }, [triadQuestions, triadVersionId]);
};

const useTriadId = () => {
    const [triadId, _setTriadId] = useState<number | string>('');
    const triadIdRef = useRef<number | string>('');
    const setTriadId = (id: number | string) => {
        triadIdRef.current = id;
        _setTriadId(id);
    };
    return { triadId, setTriadId, triadIdRef };
};


export const TriadHome = (props: TriadHomeProps) => {
    const [activeTab, setActiveTab] = useState<ActiveTab>('first');
    const [CreateTriad] = useCreateTriadMutation();
    const [month, setMonth] = useState<number | undefined>(props?.month);
    const [year, setYear] = useState<number | undefined>(props?.year);
    const [date, setDate] = useState<Date>(props?.date);
    const [selectedEmployee, setSelectedEmployee] = useState<string | null>(null);
    const [graphSelectedEmployee, setGraphSelectedEmployee] = useState<string | null>(null);
    const [triad, setTriad] = useState<any>([]);
    const [currTriad, setCurrTriad] = useState<any>(null);
    const { triadId, setTriadId, triadIdRef } = useTriadId();
    const [triadPageNev, setTriadPageNev] = useState<number>(0);
    const [shoutoutVisible, setShoutoutVisible] = useState(false);
    const [todoVisible, setTodoVisible] = useState(false);
    const userManagerLevel = useSelector(selectCurrentUserManagerLevel);
    const [isConfigured, setIsConfigured] = useState<boolean>(false);
    const useSectionWeighting = useVersionWeighting(isConfigured ? 2 : 1);

    const created = activeTab !== 'first';

    // Map legacy numeric page numbers to activeTab (used by child components)
    const setTriadPageNumber: React.Dispatch<React.SetStateAction<number>> = (page: any) => {
        if (page === 0) {
            props?.OnDisplayModal(false);
            setActiveTab('first');
            setTriadId('');
            setGraphSelectedEmployee(null);
        } else if (page === 1) {
            setActiveTab('first');
            setTriadId('');
        } else if (page === 2) {
            setActiveTab('second');
        } else if (page === 3) {
            setActiveTab('third');
        } else if (page === 4) {
            setActiveTab('newForm');
        } else if (page === 5) {
            setActiveTab('secondV2');
        } else if (page === 6) {
            setActiveTab('thirdV2');
        }
    };


    const { data: triadsData } = useGetTriadQuery(
        {
            loggedempNumber: props?.loggedEmpNo,
            tlNumber: triadId ? null : props?.hirearchy?.teamLeadId,
            associateNumber: triadId ? null : selectedEmployee,
            month: triadId ? '' : month,
            year: triadId ? '' : year,
            triadId,
        }
    );

    useEffect(() => {
        if (props?.triadRecordId) {
            setTriadId(props?.triadRecordId);
            setActiveTab('newForm');
        }
    }, [props?.triadRecordId]);

    useEffect(() => {
        if (props) {
            setSelectedEmployee(props?.hirearchy?.associateId);
            setYear(props?.year);
            setMonth(props?.month);
            setDate(props?.date);
            setIsConfigured(props?.isConfiguredNewTriad);
        }
    }, [props]);

    useEffect(() => {
        if (triadsData?.data?.length > 0) {
            setTriad(triadsData.data);
            setCurrTriad((prev: any) => {
                const id = prev?.triad_id ?? triadIdRef.current;
                const found = triadsData.data.find((t: any) => t.triad_id === id);
                return found ?? prev;
            });
        } else setTriad([]);
    }, [triadsData]);

    const renderHeader = () => (
        <div className="flex-between">
            <div>
                <h3>Coaching Observations</h3>
            </div>
            {created && (activeTab === 'third' || activeTab === 'thirdV2') && (
                <div className="d-flex justify-content-end gap-2 me-2">
                    <CoachingShoutOut
                        receiverId={props?.hirearchy?.teamLeadId}
                        receiverName={props?.hirearchy?.teamLead}
                        visible={shoutoutVisible}
                        setVisible={setShoutoutVisible}
                        coachingEntityId={currTriad?.triad_id}
                        coachingEntityTypeId={6}
                        coreValueId={0}
                    />
                    <CoachingToDo
                        receiverId={props?.hirearchy?.teamLeadId}
                        receiverName={props?.hirearchy?.teamLead}
                        visible={todoVisible}
                        setVisible={setTodoVisible}
                        coachingEntityId={currTriad?.triad_id}
                        coachingEntityTypeId={6}
                        coachingStatusId={1}
                    />
                </div>
            )}
        </div>
    );

    const handleStart = () => {
        if (!selectedEmployee || selectedEmployee === '') {
            toast.error('Please select an employee');
            return;
        }
        if (props?.hirearchy.teamLeadId === props?.loggedEmp) {
            toast.error('You cannot create a Coaching Observations for yourself');
            return;
        }
        const obj = {
            tlNumber: props?.hirearchy.teamLeadId,
            authorManagerNumber: props?.loggedEmp,
            month: props?.month,
            year: props?.year,
            associateEmployeeNumber: selectedEmployee,
            triadVersionId: isConfigured ? 2 : 1,
        };
        CreateTriad(obj)
            .unwrap()
            .then((res: any) => {
                if (res.statusCode === 200) {
                    setCurrTriad(null); 
                    setTriadId(res.data);
                    setTriadPageNumber(isConfigured ? 4 : 2);
                    toast.success('Coaching Observations Created Successfully');
                } else {
                    toast.error('Coaching Observations Creation Failed');
                }
            })
            .catch((err: any) => {
                toast.error(err);
            });
    };

    const viewAnswers = (currTriad: any) => {
        setTriadPageNumber(currTriad?.triad_version_id === 2 ? 4 : 2);
        setCurrTriad(currTriad);
        setTriadId(currTriad?.triad_id);
        setGraphSelectedEmployee(currTriad?.associate_employee_number);
        setDate(new Date(currTriad.year, currTriad.month - 1));
        setMonth(currTriad.month);
        setYear(currTriad.year);
    };

    const handleClose = () => {
        setTriadId('');
        setActiveTab('first');
        props?.OnDisplayModal(false);
    };

    return (
        <Dialog
            header={renderHeader()}
            visible={props?.displayModal}
            onHide={handleClose}
            style={{ width: '80vw', zIndex: '9998' }}
            className="card shadow bg-body rounded mt-4 border-0 p-3 w-65"
            modal
        >
            <>
                <TriadHeader
                    triadsList={triad}
                    handleDateChange={(e: any) => {
                        setDate(e.value);
                        setMonth(e.value.getMonth() + 1);
                        setYear(e.value.getFullYear());
                    }}
                    handleSelectedEmploye={setSelectedEmployee}
                    created={created}
                    hirearchy={props?.hirearchy}
                    inputMonth={month}
                    inputYear={year}
                    date={date}
                    userRole={userManagerLevel}
                    graphSelectedEmployee={graphSelectedEmployee}
                    triadPageNev={triadPageNev}
                    handleIsConfigured={(val) => setIsConfigured(val)}
                />

                {activeTab === 'first' && (
                    <TriadFirstTab
                        triadsList={triad}
                        selectedEmployee={selectedEmployee}
                        setTriadPageNumber={setTriadPageNumber}
                        setTriadPageNev={setTriadPageNev}
                        handleStart={handleStart}
                        viewAnswers={viewAnswers}
                        inputMonth={month ?? 0}
                        inputYear={year ?? 0}
                        hirearchy={props?.hirearchy}
                        isConfigured={isConfigured}
                    />
                )}

                {activeTab === 'newForm' && (
                    <TriadSecondTabV2
                        triadId={triadId}
                        key={String(triadId)}
                        currTriad={currTriad}
                        setTriadPageNumber={setTriadPageNumber}
                        onCancel={() => setTriadPageNumber(0)}
                        useSectionWeighting={useSectionWeighting}
                    />
                )}

                {activeTab === 'second' && (
                    <TriadSecondTab
                        triadId={triadId}
                        currTriad={currTriad}
                        setTriadPageNumber={setTriadPageNumber}
                    />
                )}

                {activeTab === 'third' && (
                    <TriadThirdTab
                        triadId={triadId}
                        currTriad={currTriad}
                        handleThirdTab={(val: boolean) => setActiveTab(val ? 'third' : 'first')}
                        setTriadPageNumber={setTriadPageNumber}
                    />
                )}
                {activeTab === 'thirdV2' && (
                    <TriadThirdTabV2
                        key={String(triadId)}
                        triadId={triadId}
                        currTriad={currTriad}
                        setTriadPageNumber={setTriadPageNumber} />
                )}
            </>
        </Dialog>
    );
};